// FTANodePalette.tsx — Left-hand shape palette for the FTA editor (CC ≤ 8)
import React from 'react';
import clsx from 'clsx';
import { FTANodeType } from '../../types/fta.types';
import { PALETTE_ITEMS, getTypeColors } from './ftaPaletteConfig';
import { DisplayMode } from './FTAToolbar';

// ── Tiny SVG thumbnail per shape ──────────────────────────────────────────────
function ShapeThumb({ type, fill, stroke }: { type: string; fill: string; stroke: string }) {
    const p = { fill, stroke, strokeWidth: '1.5' };
    switch (type) {
        case FTANodeType.TOP_EVENT:          return <svg viewBox="0 0 24 14"><rect x="1" y="1" width="22" height="12" rx="3" {...p} /></svg>;
        case FTANodeType.INTERMEDIATE_EVENT: return <svg viewBox="0 0 24 14"><rect x="1" y="1" width="22" height="12" {...p} /></svg>;
        case FTANodeType.BASIC_EVENT:        return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" {...p} /></svg>;
        case FTANodeType.UNDEVELOPED_EVENT:  return <svg viewBox="0 0 24 24"><polygon points="12,1 23,12 12,23 1,12" {...p} /></svg>;
        case FTANodeType.CONDITIONING_EVENT: return <svg viewBox="0 0 28 20"><ellipse cx="14" cy="10" rx="12" ry="8" {...p} /></svg>;
        case FTANodeType.HOUSE_EVENT:        return <svg viewBox="0 0 24 26"><polygon points="12,1 23,10 23,25 1,25 1,10" {...p} /></svg>;
        case FTANodeType.EXTERNAL_EVENT:     return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" {...p} /><line x1="6" y1="6" x2="18" y2="18" stroke={stroke} strokeWidth="1.5" /><line x1="18" y1="6" x2="6" y2="18" stroke={stroke} strokeWidth="1.5" /></svg>;
        case FTANodeType.AND_GATE:           return <svg viewBox="0 0 24 22"><path d={`M2 21 L2 10 A10 11 0 0 1 22 10 L22 21 Z`} {...p} /></svg>;
        case FTANodeType.OR_GATE:            return <svg viewBox="0 0 24 22"><path d="M2 21 Q12 17 22 21 L22 9 Q22 1 12 1 Q2 1 2 9 Z" {...p} /></svg>;
        case FTANodeType.XOR_GATE:           return <svg viewBox="0 0 24 22"><path d="M2 21 Q12 17 22 21 L22 9 Q22 1 12 1 Q2 1 2 9 Z" {...p} /><path d="M2 17 Q12 13 22 17" fill="none" stroke={stroke} strokeWidth="1.5" /></svg>;
        case FTANodeType.INHIBIT_GATE:       return <svg viewBox="0 0 24 24"><polygon points="12,1 22,6 22,18 12,23 2,18 2,6" {...p} /></svg>;
        case FTANodeType.PAND_GATE:          return <svg viewBox="0 0 24 22"><path d="M2 21 L2 10 A10 11 0 0 1 22 10 L22 21 Z" {...p} /><polygon points="12,11 16,18 8,18" fill={stroke} /></svg>;
        case FTANodeType.BLOCK_FUNCTION:     return <svg viewBox="0 0 28 16"><rect x="1" y="1" width="26" height="14" {...p} /></svg>;
        case FTANodeType.BLOCK_PROCESS:      return <svg viewBox="0 0 28 16"><rect x="1" y="1" width="26" height="14" {...p} /><rect x="3" y="3" width="22" height="10" fill="none" stroke={stroke} strokeWidth="0.8" strokeDasharray="2,1" /></svg>;
        case FTANodeType.BLOCK_DECISION:     return <svg viewBox="0 0 28 18"><polygon points="14,1 27,9 14,17 1,9" {...p} /></svg>;
        case FTANodeType.BLOCK_CONNECTOR:    return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" {...p} /></svg>;
        case FTANodeType.TRANSFER_IN:        return <svg viewBox="0 0 24 22"><polygon points="12,1 23,21 1,21" {...p} /><circle cx="12" cy="21" r="3" fill={stroke} /></svg>;
        case FTANodeType.TRANSFER_OUT:       return <svg viewBox="0 0 24 22"><polygon points="12,21 23,1 1,1" {...p} /><circle cx="12" cy="1" r="3" fill={stroke} /></svg>;
        default:                             return <svg viewBox="0 0 24 14"><rect x="1" y="1" width="22" height="12" {...p} /></svg>;
    }
}

// ── Section header colours ────────────────────────────────────────────────────
const SECTION_COLOR: Record<string, string> = {
    Events:   '#dc2626', Gates: '#2563eb',
    Block:    '#16a34a', Transfer: '#7c3aed',
};

interface Props {
    isDark: boolean;
    displayMode: DisplayMode;
    onAddNode: (type: FTANodeType) => void;
}

const FTANodePalette: React.FC<Props> = ({ isDark, displayMode, onAddNode }) => {
    const surface = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const sections = [...new Set(PALETTE_ITEMS.map(p => p.section))];

    return (
        <aside className={clsx('w-[70px] border-r flex flex-col items-center py-2 gap-0.5 overflow-y-auto shrink-0', surface)}>
            {sections.map(section => (
                <React.Fragment key={section}>
                    <div className="text-[8px] font-bold uppercase tracking-wider mt-2 mb-0.5 text-center px-1"
                        style={{ color: SECTION_COLOR[section] ?? '#64748b' }}>
                        {section}
                    </div>
                    {PALETTE_ITEMS.filter(p => p.section === section).map(item => {
                        const colors = getTypeColors(item.type, displayMode);
                        return (
                            <button key={item.type}
                                onClick={() => onAddNode(item.type)}
                                title={item.label}
                                className="w-12 h-11 flex flex-col items-center justify-center gap-0.5 rounded-lg border-2 transition-all hover:scale-110 hover:shadow-md"
                                style={{ backgroundColor: colors.fill, borderColor: colors.stroke }}
                            >
                                <div className="w-7 h-6 flex items-center justify-center">
                                    <ShapeThumb type={item.type} fill={colors.fill} stroke={colors.stroke} />
                                </div>
                                <span className="text-[7px] font-bold leading-tight text-center line-clamp-1"
                                    style={{ color: colors.stroke }}>
                                    {item.label.split(' ')[0]}
                                </span>
                            </button>
                        );
                    })}
                    <div className={clsx('w-8 h-px my-1', isDark ? 'bg-slate-700' : 'bg-slate-200')} />
                </React.Fragment>
            ))}
        </aside>
    );
};

export default FTANodePalette;
